import React, { useContext, useEffect, useRef, useState } from "react";
import { AlertCircle, Lock, ShieldCheck, Zap } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui";
import { triageAPI } from "../../services/api";
import {
  getIntakeSession,
  getOrCreateSessionId,
  saveIntakeSession,
} from "../../lib/triageJobsStorage";
import DocumentDropZone from "../../components/filing/DocumentDropZone";
import TriageTerminalHUD from "../../components/filing/TriageTerminalHUD";
import FilingSuccessPanel from "../../components/filing/FilingSuccessPanel";
import useTriageJobStatus from "../../hooks/useTriageJobStatus";
import { INDUSTRY_OPTIONS } from "../../components/home/ProjectedSavingsCalculator";
import { PERFORMANCE_FEE_DISCLAIMER } from "../../helpers/constants";
import { AuthContext } from "../../context/AuthProvider";
import { trackActivity, trackPageViewOnce } from "../../lib/siteActivity";

const FilingWizard = () => {
  const [searchParams] = useSearchParams();
  const industryId = searchParams.get("industry");
  const industryLabel = INDUSTRY_OPTIONS.find((o) => o.id === industryId)?.label;
  const { user, userInfo, isUserInfoLoading } = useContext(AuthContext);
  const authorizeRequestedRef = useRef(false);
  const [sessionId] = useState(() => getOrCreateSessionId());

  const [businessId, setBusinessId] = useState("");
  const [documents, setDocuments] = useState({
    assessmentNoticeUrl: "",
    assetLedgerUrl: "",
    assessmentFileName: "",
    ledgerFileName: "",
  });
  const [submitState, setSubmitState] = useState({
    isSubmitting: false,
    error: null,
    jobId: null,
  });
  const [authorizeState, setAuthorizeState] = useState({ error: null });

  useEffect(() => {
    trackPageViewOnce("/filing/start", "Filing Start");
  }, []);

  const {
    status,
    error: statusError,
    isLoading: statusLoading,
    refetch,
  } = useTriageJobStatus(submitState.jobId, {
    pauseStates: user ? [] : ["AWAITING_ACCOUNT"],
  });

  useEffect(() => {
    const cached = getIntakeSession();
    if (!cached?.jobId) return;

    setBusinessId(cached.businessId ?? "");
    setDocuments({
      assessmentNoticeUrl: cached.assessmentNoticeUrl ?? "",
      assetLedgerUrl: cached.assetLedgerUrl ?? "",
      assessmentFileName: cached.assessmentFileName ?? "",
      ledgerFileName: cached.ledgerFileName ?? "",
    });
    setSubmitState((prev) =>
      prev.jobId ? prev : { isSubmitting: false, error: null, jobId: cached.jobId }
    );
  }, []);

  const canSubmit =
    businessId.trim() &&
    documents.assessmentNoticeUrl.trim() &&
    documents.assetLedgerUrl.trim() &&
    !submitState.isSubmitting &&
    !submitState.jobId;

  const handleSubmit = async () => {
    setSubmitState({ isSubmitting: true, error: null, jobId: null });
    try {
      const { jobId } = await triageAPI.submit({
        businessId: businessId.trim(),
        assessmentNoticeUrl: documents.assessmentNoticeUrl.trim(),
        assetLedgerUrl: documents.assetLedgerUrl.trim(),
      });
      saveIntakeSession({
        sessionId,
        jobId,
        businessId: businessId.trim(),
        assessmentNoticeUrl: documents.assessmentNoticeUrl.trim(),
        assetLedgerUrl: documents.assetLedgerUrl.trim(),
        assessmentFileName: documents.assessmentFileName,
        ledgerFileName: documents.ledgerFileName,
      });
      setSubmitState({ isSubmitting: false, error: null, jobId });
    } catch (err) {
      setSubmitState({ isSubmitting: false, error: err, jobId: null });
    }
  };

  useEffect(() => {
    if (!submitState.jobId) return;
    saveIntakeSession({
      sessionId,
      jobId: submitState.jobId,
      businessId: businessId.trim(),
      assessmentNoticeUrl: documents.assessmentNoticeUrl.trim(),
      assetLedgerUrl: documents.assetLedgerUrl.trim(),
      assessmentFileName: documents.assessmentFileName,
      ledgerFileName: documents.ledgerFileName,
    });
  }, [sessionId, submitState.jobId, businessId, documents]);

  useEffect(() => {
    if (status?.state !== "AWAITING_ACCOUNT" || !submitState.jobId) return;
    if (!user || isUserInfoLoading || !userInfo) return;
    if (authorizeRequestedRef.current) return;

    authorizeRequestedRef.current = true;
    setAuthorizeState({ error: null });

    triageAPI
      .authorize(submitState.jobId)
      .then(() => refetch())
      .catch((err) => {
        authorizeRequestedRef.current = false;
        setAuthorizeState({ error: err });
      });
  }, [status?.state, submitState.jobId, user, userInfo, isUserInfoLoading, refetch]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-10 border-b border-slate-800 pb-8">
          <p className="text-xs font-semibold tracking-[0.2em] text-cyan-400 uppercase mb-3">
            Secure Business Personal Property (BPP) Tax Audit
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Upload County BPP Notice + Equipment Asset Ledger
          </h1>
          <p className="mt-3 text-slate-400 max-w-2xl leading-relaxed">
            Sixty-second encrypted portal lodging for Business Personal Property tax: machinery,
            equipment, and asset ledgers, not real estate. We flag phantom assets and depreciation
            schedule misclassifications. Secure identity validation required. No upfront retainer.
          </p>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl">{PERFORMANCE_FEE_DISCLAIMER}</p>
          {industryLabel && (
            <p className="mt-4 inline-flex items-center gap-2 border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 text-xs font-medium tracking-wide text-cyan-300 uppercase">
              Vertical pre-selected: {industryLabel}
            </p>
          )}
          <div className="mt-2 mx-auto flex flex-wrap items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-slate-400" />
              Encrypted ingestion
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
              No win, no fee
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-cyan-400" />
              Fully automated audit
            </span>
          </div>
        </header>

        {!submitState.jobId ? (
          <div className="border border-slate-800 bg-slate-900/50 p-6 sm:p-8 space-y-8">
            <label className="block">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Business / Tax Account ID <span className="text-red-400">*</span>
              </span>
              <input
                type="text"
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                placeholder="Federal EIN or county account number"
                className="mt-1.5 w-full border border-slate-600 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                required
              />
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <DocumentDropZone
                stream="assessment"
                title="Document 1: County Notice"
                subtitle="Upload County Assessment Notice (PDF)"
                accept=".pdf,application/pdf"
                uploadUrl={documents.assessmentNoticeUrl}
                fileName={documents.assessmentFileName}
                onUploadComplete={({ url, fileName }) => {
                  trackActivity({
                    event: "document_uploaded",
                    path: "/filing/start",
                    label: "assessment-notice",
                    meta: { fileName: String(fileName || "").slice(0, 120) },
                  });
                  setDocuments((prev) => ({
                    ...prev,
                    assessmentNoticeUrl: url,
                    assessmentFileName: fileName,
                  }));
                }}
              />
              <DocumentDropZone
                stream="ledger"
                title="Document 2: Equipment Asset Ledger"
                subtitle="Upload Internal Asset Ledger (PDF/CSV)"
                accept=".pdf,.csv,application/pdf,text/csv"
                uploadUrl={documents.assetLedgerUrl}
                fileName={documents.ledgerFileName}
                onUploadComplete={({ url, fileName }) => {
                  trackActivity({
                    event: "document_uploaded",
                    path: "/filing/start",
                    label: "asset-ledger",
                    meta: { fileName: String(fileName || "").slice(0, 120) },
                  });
                  setDocuments((prev) => ({
                    ...prev,
                    assetLedgerUrl: url,
                    ledgerFileName: fileName,
                  }));
                }}
              />
            </div>

            <p className="text-xs text-slate-500 border-l-2 border-slate-700 pl-3">
              Upload both files safely inside your encrypted ledger room to start your free business
              personal property tax audit. Secure corporate portal lodging.{" "}
              {PERFORMANCE_FEE_DISCLAIMER}
            </p>
            {submitState.error && (
              <div className="flex items-start gap-3 border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-300">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Submission rejected</p>
                  <p className="mt-1 opacity-90">{submitState.error.message}</p>
                </div>
              </div>
            )}

            <Button
              variant="cta"
              size="lg"
              className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold border-0"
              disabled={!canSubmit}
              isLoading={submitState.isSubmitting}
              onClick={handleSubmit}
            >
              Submit Encrypted Portal Audit
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <TriageTerminalHUD status={status} error={statusError} isLoading={statusLoading} />
            {status?.state === "AWAITING_ACCOUNT" && (
              <>
                <FilingSuccessPanel
                  status={status}
                  variant="preview"
                  sessionId={sessionId}
                  jobId={submitState.jobId}
                  businessId={businessId}
                  assessmentNoticeUrl={documents.assessmentNoticeUrl}
                  assetLedgerUrl={documents.assetLedgerUrl}
                />
                {user && authorizeState.error && (
                  <div className="flex items-start gap-3 border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-300">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Authorization failed</p>
                      <p className="mt-1 opacity-90">{authorizeState.error.message}</p>
                    </div>
                  </div>
                )}
              </>
            )}
            {status?.state === "FILED" && <FilingSuccessPanel status={status} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilingWizard;
